import { computed, inject, Injectable, signal } from '@angular/core';
import { cancelableResource } from '@softline/core';
import { CustomerRepository } from '../repositories/customer.repository';
import { CustomerCard, CustomerFuelCard } from '../types/card';
import { CustomerCollector } from '../types/collector';
import { CustomerConditionSheetEntry } from '../types/condition-sheet';
import { CustomerContact } from '../types/contact';
import { CustomerResponse } from '../types/customer';
import { CustomerNote } from '../types/note';

/** Read-only state for a single customer's detail, contacts, collectors,
    condition sheets, cards, fuel cards and notes — all keyed off `customerId`.
    Drive it by setting `customerId`; every resource refetches automatically. */
@Injectable()
export class CustomerService {
  private readonly customerRepository = inject(CustomerRepository);

  /** Source of truth — set by the consumer; null clears every resource. */
  readonly customerId = signal<string | null>(null);

  /** Base customer detail + loading flag (aliases for `customerDetail` /
      `customerDetailLoading`). */
  readonly customer = computed<CustomerResponse | null | undefined>(() =>
    this.detailResource.value()
  );
  readonly isLoading = computed(() => this.detailResource.status() === 'loading');

  private readonly detailError = signal(false);

  private readonly detailResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.detailError.set(false); return null; }
      try {
        const result = await this.customerRepository.getById(id);
        this.detailError.set(false);
        return result;
      } catch {
        this.detailError.set(true);
        return null;
      }
    },
  });
  readonly customerDetail = computed<CustomerResponse | null | undefined>(() =>
    this.detailResource.value()
  );
  readonly customerDetailLoading = computed(
    () => this.detailResource.status() === 'loading'
  );
  readonly customerDetailError = this.detailError.asReadonly();

  private readonly contactsError = signal(false);
  private readonly contactsResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.contactsError.set(false); return []; }
      try {
        const result = await this.customerRepository.getContacts(id);
        this.contactsError.set(false);
        return result;
      } catch {
        this.contactsError.set(true);
        return [];
      }
    },
  });
  readonly customerContacts = computed<CustomerContact[]>(
    () => this.contactsResource.value() ?? []
  );
  readonly customerContactsLoading = computed(
    () => this.contactsResource.status() === 'loading'
  );
  readonly customerContactsError = this.contactsError.asReadonly();

  private readonly collectorsError = signal(false);
  private readonly collectorsResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.collectorsError.set(false); return []; }
      try {
        const result = await this.customerRepository.getCollectors(id);
        this.collectorsError.set(false);
        return result;
      } catch {
        this.collectorsError.set(true);
        return [];
      }
    },
  });
  readonly customerCollectors = computed<CustomerCollector[]>(
    () => this.collectorsResource.value() ?? []
  );
  readonly customerCollectorsLoading = computed(
    () => this.collectorsResource.status() === 'loading'
  );
  readonly customerCollectorsError = this.collectorsError.asReadonly();

  private readonly conditionsError = signal(false);
  private readonly conditionsResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.conditionsError.set(false); return []; }
      try {
        const result = await this.customerRepository.getConditionsSheets(id);
        this.conditionsError.set(false);
        return result;
      } catch {
        this.conditionsError.set(true);
        return [];
      }
    },
  });
  readonly customerConditions = computed<CustomerConditionSheetEntry[]>(
    () => this.conditionsResource.value() ?? []
  );
  readonly customerConditionsLoading = computed(
    () => this.conditionsResource.status() === 'loading'
  );
  readonly customerConditionsError = this.conditionsError.asReadonly();

  private readonly cardsError = signal(false);
  private readonly cardsResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.cardsError.set(false); return []; }
      try {
        const result = await this.customerRepository.getCards(id);
        this.cardsError.set(false);
        return result;
      } catch {
        this.cardsError.set(true);
        return [];
      }
    },
  });
  readonly customerCards = computed<CustomerCard[]>(
    () => this.cardsResource.value() ?? []
  );
  readonly customerCardsLoading = computed(
    () => this.cardsResource.status() === 'loading'
  );
  readonly customerCardsError = this.cardsError.asReadonly();

  private readonly fuelCardsError = signal(false);
  private readonly fuelCardsResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.fuelCardsError.set(false); return []; }
      try {
        const result = await this.customerRepository.getFuelCards(id);
        this.fuelCardsError.set(false);
        return result;
      } catch {
        this.fuelCardsError.set(true);
        return [];
      }
    },
  });
  readonly customerFuelCards = computed<CustomerFuelCard[]>(
    () => this.fuelCardsResource.value() ?? []
  );
  readonly customerFuelCardsLoading = computed(
    () => this.fuelCardsResource.status() === 'loading'
  );
  readonly customerFuelCardsError = this.fuelCardsError.asReadonly();

  private readonly notesError = signal(false);
  private readonly notesResource = cancelableResource({
    params: () => this.customerId(),
    loader: async ({ params: id }) => {
      if (!id) { this.notesError.set(false); return []; }
      try {
        const result = await this.customerRepository.getNotes(id);
        this.notesError.set(false);
        return result;
      } catch {
        this.notesError.set(true);
        return [];
      }
    },
  });
  readonly customerNotes = computed<CustomerNote[]>(
    () => this.notesResource.value() ?? []
  );
  readonly customerNotesLoading = computed(
    () => this.notesResource.status() === 'loading'
  );
  readonly customerNotesError = this.notesError.asReadonly();

  /* ---- imperative one-shot loads (for callers that need data on demand
     rather than via the reactive `customerId` resources) ---- */

  async loadNotes(id: string): Promise<CustomerNote[]> {
    return this.customerRepository.getNotes(id);
  }

  async loadMainContact(id: string): Promise<CustomerContact> {
    return this.customerRepository.getMainContact(id);
  }

  async loadContacts(id: string): Promise<CustomerContact[]> {
    return this.customerRepository.getContacts(id);
  }

  async loadCollectors(id: string): Promise<CustomerCollector[]> {
    return this.customerRepository.getCollectors(id);
  }

  async loadCards(id: string): Promise<CustomerCard[]> {
    return this.customerRepository.getCards(id);
  }

  async loadFuelCards(id: string): Promise<CustomerFuelCard[]> {
    return this.customerRepository.getFuelCards(id);
  }

  async loadConditionsSheets(id: string): Promise<CustomerConditionSheetEntry[]> {
    return this.customerRepository.getConditionsSheets(id);
  }

  async loadByBarcode(type: string, code: string): Promise<CustomerResponse> {
    return this.customerRepository.getByBarcode(type, code);
  }
}
